import React from 'react';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';

const NearbyAttractions = () => {
  const attractions = [
    {
      id: 1,
      name: "Supa Dam",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4",
      description: "A massive hydroelectric dam offering breathtaking views of the reservoir and surrounding hills",
      distance: "8 km",
      duration: "1-2 hours",
      type: "Scenic Spot",
      highlights: ["Photography", "Sunset Views", "Boating", "Picnic Spot"]
    },
    {
      id: 2,
      name: "Syntheri Rocks",
      image: "https://images.unsplash.com/photo-1506469717960-433cebe3f181",
      description: "Unique monolithic granite rocks formed by volcanic activity, perfect for rock climbing",
      distance: "12 km",
      duration: "2-3 hours", 
      type: "Adventure Site",
      highlights: ["Rock Climbing", "Geology Study", "Nature Walk", "Photography"]
    },
    {
      id: 3,
      name: "Kavala Caves",
      image: "https://images.unsplash.com/photo-1551632811-561732d1e306",
      description: "Ancient limestone caves with stalactites and stalagmites, rich in history and natural beauty",
      distance: "15 km",
      duration: "2-3 hours",
      type: "Historical Site", 
      highlights: ["Cave Exploration", "Ancient History", "Natural Formations", "Adventure"]
    },
    {
      id: 4,
      name: "Ulavi Temple",
      image: "https://images.unsplash.com/photo-1581833971358-2c8b550f87b3",
      description: "Sacred temple dedicated to Channabasaveshwara, surrounded by lush greenery",
      distance: "20 km",
      duration: "1-2 hours",
      type: "Religious Site",
      highlights: ["Spiritual Experience", "Architecture", "Peace & Tranquility", "Cultural Heritage"]
    },
    {
      id: 5,
      name: "Anshi National Park",
      image: "https://images.unsplash.com/photo-1549366021-9f761d040a94",
      description: "Part of Kali Tiger Reserve, home to diverse wildlife including tigers, elephants, and birds",
      distance: "25 km", 
      duration: "4-6 hours",
      type: "Wildlife Sanctuary",
      highlights: ["Wildlife Safari", "Bird Watching", "Nature Photography", "Conservation Education"]
    },
    {
      id: 6,
      name: "Dudhsagar Falls",
      image: "https://images.unsplash.com/photo-1586276393635-5ecd8112e2de",
      description: "India's second-highest waterfall, creating a milky cascade effect during monsoons",
      distance: "35 km",
      duration: "Full Day",
      type: "Natural Wonder",
      highlights: ["Waterfall Trek", "Train Journey", "Swimming", "Photography"]
    }
  ];

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
            Nearby Attractions
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto font-body">
            Discover the incredible destinations within reach of your Dandeli adventure base
          </p>
        </div>

        {/* Attractions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {attractions?.map((attraction) => (
            <div
              key={attraction?.id}
              className="bg-card rounded-2xl shadow-organic overflow-hidden hover-lift group"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={attraction?.image}
                  alt={attraction?.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-organic duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                
                {/* Type Badge */}
                <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-heading font-semibold">
                  {attraction?.type}
                </div>

                {/* Distance */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center">
                  <Icon name="MapPin" size={12} className="text-primary mr-1" />
                  <span className="text-sm font-heading font-semibold text-text-primary">
                    {attraction?.distance}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-2">
                  {attraction?.name}
                </h3>
                
                <p className="text-text-secondary mb-4 font-body text-sm">
                  {attraction?.description}
                </p>

                {/* Details */}
                <div className="flex items-center justify-between mb-4 text-sm">
                  <div className="flex items-center">
                    <Icon name="Clock" size={14} className="text-primary mr-1" />
                    <span className="text-text-secondary font-caption">{attraction?.duration}</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Navigation" size={14} className="text-primary mr-1" />
                    <span className="text-text-secondary font-caption">{attraction?.distance} away</span>
                  </div>
                </div>

                {/* Highlights */}
                <div className="mb-4">
                  <h4 className="text-sm font-heading font-semibold text-text-primary mb-2">
                    Highlights:
                  </h4>
                  <div className="grid grid-cols-2 gap-1">
                    {attraction?.highlights?.slice(0, 4)?.map((highlight, index) => (
                      <div key={index} className="flex items-center text-xs text-text-secondary">
                        <Icon name="Star" size={10} className="text-accent mr-1 flex-shrink-0" />
                        <span className="font-caption">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Button */}
                <Button
                  variant="outline"
                  size="default"
                  iconName="Navigation"
                  iconPosition="left"
                  fullWidth
                  onClick={() => {
                    // Open Google Maps for directions
                    const searchQuery = encodeURIComponent(`${attraction?.name} Dandeli Karnataka`);
                    window.open(`https://www.google.com/maps/search/${searchQuery}`, '_blank');
                  }}
                  className="border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground"
                >
                  Get Directions
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12 bg-muted rounded-2xl p-8">
          <h3 className="text-2xl font-heading font-bold text-text-primary mb-4">
            Plan Your Perfect Itinerary
          </h3>
          <p className="text-text-secondary mb-6 font-body max-w-2xl mx-auto">
            Need help planning visits to these attractions? Our local experts can create a customized itinerary for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="default"
              size="lg"
              iconName="MessageCircle"
              iconPosition="left"
              onClick={() => {
                const message = encodeURIComponent("Hi! I'd like to plan visits to nearby attractions during my stay. Can you help me create an itinerary?");
                window.open(`https://wa.me/918277385225?text=${message}`, '_blank');
              }}
            >
              Plan with WhatsApp
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Phone"
              iconPosition="left"
              onClick={() => window.open('tel:+918277385225', '_self')}
              className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
            >
              Call for Guidance
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NearbyAttractions;